import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { VariableNames } from '@app/eds/constants';
import { TranslateService } from '@ngx-translate/core';

export type multiSelectKeyValueType = {
  name: string;
  value: string;
};

@Component({
  selector: 'app-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss']
})
export class MultiSelectComponent implements OnInit, OnChanges {
  // IsAllSelected stores the value if all the data is selected or not.
  isAllSelected = false;

  // SelectedItemList stores the value of the items which are selected.
  @Input() selectedItemList: string[] = [];

  // This dataset will be used as options in select and it should be passed as [{ value: 'steak-0', name: 'Steak' }].
  @Input() data: multiSelectKeyValueType[] = [];

  // to disable dropdown or not
  @Input() isDisabled = false;

  // Event emiiter when a dropdown value is selected.
  @Output() multiSelectChangeHandler = new EventEmitter();

  // select view child stores mat select.
  @ViewChild('select')
  select!: MatSelect;

  // stores the previous selected item list
  previousSelectedItemList: string[] = [];

  constructor(private readonly translate: TranslateService) { }

  ngOnInit(): void {
    this.previousSelectedItemList = this.selectedItemList
      ? [...this.selectedItemList]
      : [];
    this.checkIsAllSelected();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes[VariableNames.SELECTED_ITEM_LIST]) {
      this.previousSelectedItemList = this.selectedItemList
        ? [...this.selectedItemList]
        : [];
    }

    this.checkIsAllSelected();
  }

  /**
   * This checks all the values are pre-selected or not.
   */
  checkIsAllSelected = () => {
    this.isAllSelected = true;
    this.data?.forEach((x) => {
      this.isAllSelected &&= this.selectedItemList.includes(x.value);
    });
  };

  /**
   * This methods is used to toggle select and deselect button data.
   */
  toggleAllSelection = () => {
    this.isAllSelected = !this.isAllSelected;

    this.select.options.forEach((item: MatOption) => {
      if (this.isAllSelected) {
        item.select();
        if (this.selectedItemList.indexOf(item.value) === -1) {
          this.selectedItemList.push(item.value);
        }
      } else {
        item.deselect();
        this.selectedItemList.pop();
      }
    });
  };

  /**
   * This method return count of data selected
   *
   * @returns {string} count with selected text
   */
  getSelectedCount = () =>
    this.previousSelectedItemList?.length === this.data?.length
      ? this.translate.instant('buyin.priority.all')
      : `${this.previousSelectedItemList?.length} ${this.translate.instant(
        'multiSelect.selected'
      )}`;

  /**
   * This handles closing of options when clicked outside
   *
   * @param {boolean} event
   */
  closeOptions = (event: boolean) => {
    if (!event) {
      this.selectedItemList = this.previousSelectedItemList;
      this.checkIsAllSelected();
    }
  };

  /**
   * This method return select/deselect text
   *
   * @returns {string} select/deselect text
   */
  getSelectText = () =>
    this.isAllSelected ? 'multiSelect.deSelectAll' : 'multiSelect.selectAll';

  /**
   * This method is emitted when apply button is triggered
   */
  applyClicked = () => {
    this.previousSelectedItemList = [...this.selectedItemList];
    this.multiSelectChangeHandler.emit(this.selectedItemList);
    this.select.close();
  };

  /**
   * This method returns the panel class name.
   *
   * @returns {string} classname
   */
  getClassName = () => {
    if (this.isDisabled) {
      return 'disabled-multi-select';
    } else {
      return this.select?.panelOpen ? 'panel-grey' : 'panel-white';
    }
  };
}
